/**
 * Converts pasted HTML (e.g. copied from Microsoft Word, Google Docs, or any
 * rich text editor) into WhatsApp's plain-text markdown-style formatting:
 * bold -> *text*, italic -> _text_, strikethrough -> ~text~,
 * monospace -> ```text```, list items -> "- " bullets, paragraphs/line
 * breaks -> newlines. WhatsApp templates only support this flat text
 * markup, not real rich text, so this is a one-way, lossy conversion by
 * design — anything without a WhatsApp equivalent (headings, tables, links,
 * images, colors, fonts) is flattened to its plain text content.
 */
export function htmlToWhatsAppMarkdown(html: string): string {
  if (typeof DOMParser === 'undefined') {
    // Non-browser environment (native) — nothing to parse, caller should
    // fall back to plain text.
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const output = walk(doc.body).trim();

  // Collapse 3+ blank lines down to at most one, and trim trailing spaces
  // per line, since paragraph-to-newline conversion tends to over-produce.
  return output
    .split('\n')
    .map(line => line.replace(/[ \t]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
}

const BOLD_TAGS = new Set(['b', 'strong']);
const ITALIC_TAGS = new Set(['i', 'em']);
const STRIKE_TAGS = new Set(['s', 'strike', 'del']);
const MONO_TAGS = new Set(['code', 'pre', 'tt']);
const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'tr']);

function walk(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '').replace(/\s+/g, match => (match.includes('\n') ? '\n' : ' '));
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') return '\n';
  if (tag === 'script' || tag === 'style') return '';

  const isBold = BOLD_TAGS.has(tag) || (el.style && (el.style.fontWeight === 'bold' || parseInt(el.style.fontWeight || '0', 10) >= 700));
  const isItalic = ITALIC_TAGS.has(tag) || (el.style && el.style.fontStyle === 'italic');
  const isStrike = STRIKE_TAGS.has(tag) || (el.style && (el.style.textDecoration || '').includes('line-through'));
  const isMono = MONO_TAGS.has(tag);
  const isListItem = tag === 'li';

  let childText = Array.from(el.childNodes).map(walk).join('');

  if (isListItem) {
    // Normalize interior whitespace/newlines from nested block content within a bullet
    childText = childText.trim().replace(/\n+/g, ' ');
    return `- ${childText}\n`;
  }

  if (isMono && childText.trim()) {
    childText = `\`\`\`${childText.trim()}\`\`\``;
  } else {
    if (isStrike && childText.trim()) childText = wrapPreservingWhitespace(childText, '~');
    if (isItalic && childText.trim()) childText = wrapPreservingWhitespace(childText, '_');
    if (isBold && childText.trim()) childText = wrapPreservingWhitespace(childText, '*');
  }

  if (BLOCK_TAGS.has(tag) && childText.trim()) {
    return `${childText}\n`;
  }

  return childText;
}

// Wrapping "  bold text  " as "  *bold text*  " (not "*  bold text  *") keeps
// WhatsApp's renderer happy — markers must hug the actual word content.
function wrapPreservingWhitespace(text: string, marker: string): string {
  const match = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return `${marker}${text}${marker}`;
  const [, leading, core, trailing] = match;
  if (!core) return text;
  return `${leading}${marker}${core}${marker}${trailing}`;
}
